// resources/js/Pages/Formula/InlineEditor.js - UPDATED WITH TINYMCE SUPPORT
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import LocalTinyMCE from '../../Components/LocalTinyMCE';

const InlineEditor = forwardRef(function InlineEditor({ 
  value, 
  onChange, 
  placeholder = "Click to edit...",
  className = "",
  multiline = false,
  richText = false, // ✅ NEW: Enable TinyMCE rich text editing
  disabled = false,
  onEditStart = null,
  onEditEnd = null,
  autoFocus = false,
  onKeyDown = null
}, ref) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const tinyMCERef = useRef(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    startEditing: () => startEditing(),
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  // Auto-focus when autoFocus is true
  useEffect(() => {
    if (autoFocus && !isEditing) {
      startEditing();
    }
  }, [autoFocus]);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  // Focus and select when editing starts (for non-TinyMCE)
  useEffect(() => {
    if (isEditing && !richText && inputRef.current) {
      inputRef.current.focus();
      if (!multiline) {
        inputRef.current.select();
      }
    }
  }, [isEditing, multiline, richText]);

  // ✅ UPDATED: Global click handler - handle TinyMCE differently
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!isEditing) return;
      
      if (richText) {
        // For TinyMCE, check if click is outside the entire TinyMCE interface
        const tinyMCEContainer = document.querySelector('.tox-tinymce');
        const tinyMCEMenus = document.querySelectorAll('.tox-menu, .tox-dialog, .tox-toolbar, .tox-statusbar');
        
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          // Check if click is in any TinyMCE UI elements
          let isInTinyMCE = false;
          if (tinyMCEContainer && tinyMCEContainer.contains(e.target)) {
            isInTinyMCE = true;
          }
          tinyMCEMenus.forEach(menu => {
            if (menu && menu.contains(e.target)) {
              isInTinyMCE = true;
            }
          });
          
          if (!isInTinyMCE) {
            saveChanges();
          }
        }
      } else {
        // Standard handling for regular inputs
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          saveChanges();
        }
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleGlobalClick);
      return () => document.removeEventListener('mousedown', handleGlobalClick);
    }
  }, [isEditing, editValue, richText]);

  const startEditing = () => {
    if (disabled) return;
    setEditValue(value || '');
    setIsEditing(true);
    onEditStart?.();
  };

  const saveChanges = () => {
    const trimmedValue = richText ? editValue : editValue.trim();
    onChange(trimmedValue);
    setIsEditing(false);
    onEditEnd?.();
  };

  const cancelChanges = () => {
    setEditValue(value || '');
    setIsEditing(false);
    onEditEnd?.();
  };

  const handleKeyDown = (e) => {
    if (richText) return; // TinyMCE handles its own keyboard events
    
    // Call custom onKeyDown handler if provided
    if (onKeyDown) {
      onKeyDown(e);
      if (e.defaultPrevented) return;
    }
    
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      saveChanges();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelChanges();
    }
  };

  const handleClick = (e) => {
    if (!isEditing) {
      e.stopPropagation();
      startEditing();
    }
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  // ✅ NEW: Handle TinyMCE changes
  const handleTinyMCEChange = (content) => {
    setEditValue(content);
  };

  // ✅ NEW: Handle TinyMCE blur
  const handleTinyMCEBlur = () => {
    // Small delay to allow TinyMCE to handle internal navigation
    setTimeout(() => {
      saveChanges();
    }, 100);
  };

  const handleBlur = (e) => {
    if (richText) return; // TinyMCE has its own blur handler
    
    // Small delay to allow click events to register first
    setTimeout(() => {
      if (isEditing) {
        saveChanges();
      }
    }, 0);
  };

  // ✅ RENDER EDITING STATE
  if (isEditing) {
    const isInlineStyle = className.includes('field-instruction-inline') || className.includes('d-inline');
    const isKeyEditor = className.includes('field-key-editor');
    
    if (richText) {
      // ✅ TinyMCE Rich Text Editor
      return (
        <div ref={containerRef} className="inline-editor-container">
          <LocalTinyMCE
            ref={tinyMCERef}
            value={editValue}
            onChange={handleTinyMCEChange}
            onBlur={handleTinyMCEBlur}
            placeholder={placeholder}
            height={isInlineStyle ? 150 : 200}
            menubar={false}
            toolbar={isInlineStyle ? 
              "bold italic | link | bullist numlist | removeformat" : 
              "bold italic underline | link | bullist numlist | removeformat"
            }
            className={`inline-editor-tinymce ${className}`}
          />
        </div>
      );
    } else {
      // ✅ Standard Input/Textarea - optimized for key editor
      const InputComponent = multiline ? 'textarea' : 'input';
      return (
        <span ref={containerRef} className="inline-editor-container" style={{ display: isKeyEditor ? 'inline' : 'block' }}>
          <InputComponent
            ref={inputRef}
            type={multiline ? undefined : "text"}
            value={editValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`form-control form-control-sm inline-editor-input ${className}`}
            placeholder={placeholder}
            rows={multiline ? 3 : undefined}
            style={{
              minHeight: isKeyEditor ? 'auto' : (multiline ? '80px' : '32px'),
              resize: multiline ? 'vertical' : 'none',
              display: isKeyEditor ? 'inline' : 'block',
              width: isKeyEditor ? 'auto' : '100%',
              minWidth: isKeyEditor ? '100px' : 'auto',
              padding: isKeyEditor ? '2px 6px' : '4px 8px',
              fontSize: isKeyEditor ? 'inherit' : '0.875rem',
              fontWeight: isKeyEditor ? '600' : 'normal',
              whiteSpace: isKeyEditor ? 'nowrap' : 'normal'
            }}
          />
        </span>
      );
    }
  }

  // ✅ RENDER VIEW STATE
  const displayValue = value || placeholder;
  const isEmpty = !value || value.trim() === '';
  const isInlineStyle = className.includes('field-instruction-inline') || className.includes('d-inline');
  const isKeyEditor = className.includes('field-key-editor');

  return (
    <div ref={containerRef} className="inline-editor-container">
      <span 
        className={`inline-editable ${className} ${isEmpty ? 'empty' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleClick}
        style={{ 
          cursor: disabled ? 'default' : 'pointer',
          minHeight: richText ? (isInlineStyle ? '24px' : '40px') : (isKeyEditor ? 'auto' : '1.5em'),
          display: isKeyEditor ? 'inline' : (isInlineStyle ? 'inline-block' : 'block'),
          width: isKeyEditor ? 'auto' : (isInlineStyle ? 'auto' : '100%'),
          minWidth: isKeyEditor ? 'auto' : (isInlineStyle ? 'auto' : '60px'),
          padding: isKeyEditor ? '2px 4px' : (richText ? (isInlineStyle ? '2px 4px' : '8px 12px') : '4px 6px'),
          borderRadius: '4px',
          border: '1px solid transparent',
          wordBreak: isKeyEditor ? 'keep-all' : 'break-word',
          lineHeight: isKeyEditor ? '1.2' : (isInlineStyle ? '1.3' : '1.4'),
          backgroundColor: richText && !isEmpty ? (isInlineStyle ? 'transparent' : '#f8f9fa') : 'transparent',
          verticalAlign: isKeyEditor || isInlineStyle ? 'baseline' : 'baseline',
          whiteSpace: isKeyEditor ? 'nowrap' : 'normal'
        }}
        title={disabled ? '' : "Click to edit"}
      >
        {richText && !isEmpty ? (
          // ✅ Render HTML content for rich text
          <span 
            dangerouslySetInnerHTML={{ __html: displayValue }}
            style={{ 
              minHeight: isInlineStyle ? '20px' : '20px',
              display: 'inline'
            }}
          />
        ) : (
          // ✅ Render plain text
          <span style={{ 
            whiteSpace: isKeyEditor ? 'nowrap' : (richText ? 'normal' : 'pre-wrap'),
            display: 'inline'
          }}>
            {displayValue}
          </span>
        )}
      </span>
    </div>
  );
});

export default InlineEditor;